/**
 * Display labels for activity feed grouping.
 *
 * 6 active categories (carousel sections):
 *   document (Documenti), tracking (Tracking), issue (Issue),
 *   email (Email), contact (Rubrica), agent (Sistema)
 *
 * Grouping is by category ONLY — one carousel section per category.
 */

import type { ActivityType } from "@/types/activity";

const CATEGORY_LABELS: Record<string, string> = {
    document: "Documenti",
    tracking: "Tracking",
    issue: "Issue",
    email: "Email",
    contact: "Rubrica",
    agent: "Sistema",
    // Legacy (old data may still have these categories)
    notification: "Sistema",
    workflow: "Sistema",
    expedition: "Tracking",
    validation: "Documenti",
    other: "Altro",
};

const TYPE_LABELS: Record<ActivityType, string> = {
    email_new_shipment_request: "Nuova richiesta",
    email_attention_required: "Richiede attenzione",
    document_updated: "Aggiornamento",
    status_update: "Aggiornamento stato",
    expedition_status_changed: "Cambio stato",
    expedition_modified: "Modifica",
    customs_issue: "Problema doganale",
    seal_change: "Cambio sigillo",
    resolution_invite: "Verifica richiesta",
    new_order_request: "Nuova richiesta ordine",
    contact_added: "Nuovo contatto",
};

/**
 * Returns display label for activity category.
 */
export function getCategoryLabel(category: string | undefined): string {
    if (!category) return CATEGORY_LABELS.other;
    const key = category.toLowerCase();
    return CATEGORY_LABELS[key] ?? category.charAt(0).toUpperCase() + category.slice(1);
}

/**
 * Returns display label for activity type.
 */
export function getTypeLabel(type: ActivityType): string {
    return TYPE_LABELS[type] ?? type;
}

/**
 * Returns section header label — just the category label (no more category+type).
 */
export function getGroupLabel(category: string | undefined, _type?: ActivityType): string {
    return getCategoryLabel(category);
}

/**
 * 6 active categories to fetch for system activity logs (one paginated request per category).
 */
export const SYSTEM_ACTIVITY_CATEGORIES: string[] = [
    "document",
    "tracking",
    "issue",
    "email",
    "contact",
    "agent",
];

/**
 * Order of categories for sorting groups.
 * UI order: Documenti, Tracking, Issue, Email, Rubrica, Sistema.
 */
export const CATEGORY_ORDER: string[] = [
    "document",
    "tracking",
    "issue",
    "email",
    "contact",
    "agent",
];

// ============================================================================
// Actor Display Names
// ============================================================================

const ACTOR_DISPLAY_NAMES: Record<string, string> = {
    eve_mailer: 'EveMailer',
    eve_care: 'EveCare',
    eve_read: 'EveRead',
    eve_alert: 'EveAlert',
    eve_track: 'EveTrack',
    eve_doc: 'EveDoc',
    eve_comply: 'EveComply',
    eve_notify: 'EveNotify',
    system: 'Sistema',
    user: 'Utente',
    DocumentSingleCheck: 'Validazione automatica',
};

export function getActorDisplayName(actorName: string, sourceType?: string): string {
    const mapped = ACTOR_DISPLAY_NAMES[actorName];
    if (mapped) return mapped;
    if (actorName.startsWith('eve_')) {
        return 'Eve' + actorName.slice(4).charAt(0).toUpperCase() + actorName.slice(5);
    }
    if (sourceType === 'external') return 'Sistema esterno';
    if (sourceType === 'system') return 'Sistema';
    return actorName;
}

export function getCategorySortIndex(category: string | undefined): number {
    if (!category) return CATEGORY_ORDER.length;
    const key = category.toLowerCase();
    // Map legacy categories to their new equivalents for sorting
    const legacyMap: Record<string, string> = {
        validation: "document",
        expedition: "tracking",
        notification: "agent",
        workflow: "agent",
    };
    const mapped = legacyMap[key] || key;
    const idx = CATEGORY_ORDER.indexOf(mapped);
    return idx >= 0 ? idx : CATEGORY_ORDER.length;
}
